import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useState } from 'react';
import { useAsync, useInterval } from 'react-use';
import { Link } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import { Box } from '@material-ui/core';
import { relativeTime } from '../releases/relativeTime';
import {
  ReleaseDeployment,
  releaseDeployment,
} from '../releases/deploymentState';
import { useDeploymentStates } from '../releases/useDeploymentStates';

/**
 * The portal's own entity. Hardcoded rather than configured: a frontend config
 * key would have to be declared frontend-visible in a config schema to be
 * readable here at all, which is a lot of machinery for one constant that changes
 * only if this repository's own catalog-info.yaml is renamed.
 */
const SELF_ENTITY_REF = 'component:cloudbear/backstage';

/**
 * How often to look again.
 *
 * The homepage is the tab people leave open, so a banner that only checks on
 * load would announce a release once and then go stale in both directions: it
 * would miss a release cut this afternoon, and keep nagging about one that has
 * since been deployed.
 *
 * Five minutes rather than anything faster. A release cannot appear in the portal
 * until the ingest sweep has run (`releases.ingest.schedule`, every 30 minutes by
 * default), so polling more often mostly re-asks a question whose answer cannot
 * have changed; the deploy state itself does change within a rollout, which is
 * what makes five minutes worth having over thirty. Two small requests per
 * interval.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

type SelfRelease = {
  tagName: string;
  commitSha?: string;
  releasedAt: string;
  webUrl?: string;
};

/**
 * Whether a release is worth interrupting the front page for.
 *
 * Only two states are: `not-deployed`, where the release exists and the source's
 * history is recent enough to have seen it and has not, and `failed`, where the
 * deploy was attempted and did not work. Everything else is either fine or
 * unknown, and a banner that appears for ordinary states stops being read.
 *
 * Separated from the component so the rule is testable: the banner cannot be
 * exercised locally, because the portal is deployed by CAP and a local stack has
 * no CAP credential, so the state is always unknown there.
 */
export function shouldAnnounce(
  deployment: ReleaseDeployment | undefined,
): boolean {
  return (
    deployment?.status === 'not-deployed' || deployment?.status === 'failed'
  );
}

/**
 * Says when Backstage itself has a release that is not live yet.
 *
 * Dogfooding the deployment join: the newest release of this repository either
 * matches the newest deployment the source recorded, or it does not, and "does
 * not" is worth saying on the front page because the person most likely to act
 * on it is already looking at it.
 *
 * Deliberately quiet in every other case. It renders nothing when the newest
 * release is live, when it is mid-deploy, when nothing is known (no cluster, no
 * CAP credential, or a release older than the retained history), and when the
 * releases feed has not ingested anything yet. A banner that appears for
 * ordinary states stops being read.
 */
export const SelfReleaseBanner = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  // Both halves have to move together: a new release with a stale deployment
  // state would read as undeployed, and a fresh deployment state against a stale
  // release would keep the banner up after the deploy landed.
  const [tick, setTick] = useState(0);
  useInterval(() => setTick(current => current + 1), POLL_INTERVAL_MS);

  const { states } = useDeploymentStates([SELF_ENTITY_REF], { nonce: tick });

  const { value } = useAsync(async (): Promise<SelfRelease | undefined> => {
    const [kind, rest] = SELF_ENTITY_REF.split(':');
    const [namespace, name] = rest.split('/');
    const baseUrl = await discoveryApi.getBaseUrl('releases');
    const response = await fetch(
      `${baseUrl}/entity/${encodeURIComponent(namespace)}/${encodeURIComponent(
        kind,
      )}/${encodeURIComponent(name)}?limit=1`,
    );
    if (!response.ok) {
      // Silent: a front-page banner is not the place to report that a feed is
      // unavailable.
      return undefined;
    }
    return (await response.json()).releases?.[0];
  }, [tick, discoveryApi, fetch]);

  if (!value) {
    return null;
  }

  const deployment = releaseDeployment({
    release: value,
    state: states[SELF_ENTITY_REF],
  });

  if (!shouldAnnounce(deployment)) {
    return null;
  }

  const failed = deployment?.status === 'failed';

  return (
    <Box width="100%" pb={2}>
      <Alert severity={failed ? 'error' : 'info'}>
        {`Backstage ${value.tagName} was released ${relativeTime(
          value.releasedAt,
        )} and ${
          failed ? 'its deploy failed' : 'is not deployed yet'
        }. This portal is still running the previous version. `}
        {value.webUrl && <Link to={value.webUrl}>Release notes</Link>}
      </Alert>
    </Box>
  );
};
